import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import Auth from "../../Auth";
import { useAppStore } from "../../stores/appStore";
import { useProjectStore } from "../../stores/projectStore";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import HistoryModal from "../HistoryModal/HistoryModal";
import TiptapListField, { oldBeatsToTiptap } from "../TiptapListField";

const SelectedItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  .timestamp {
    color: var(--grey-1);
  }
  .tip-tap-ordered-list-field-container {
    border: none;
  }
`;

function SelectedItemDisplay({ selectedItem }) {
  const userId = useAppStore((state) => state.user?.uid);
  const projectId = useProjectStore((state) => state.project?.id);

  if (!selectedItem) {
    return null;
  }

  return (
    <div key={selectedItem.key}>
      <div
        className="timestamp"
        style={{ marginBottom: "10px" }}
      >
        {moment(selectedItem.createdAt).format("LLL")}
      </div>
      <TiptapListField
        value={selectedItem.tiptapContent}
        readOnly
        userId={userId}
        projectId={projectId}
      />
    </div>
  );
}

function ScenesHistory({ documentId, onRestore }) {
  const [historicalRecords, setHistoricalRecords] = useState([]);
  const [historicalBeats, setHistoricalBeats] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const scenesHistoryOpen = useStoryBibleStore((state) => state.scenesHistoryOpen);
  const setScenesHistoryOpen = useStoryBibleStore((state) => state.setScenesHistoryOpen);
  const project = useProjectStore((state) => state.project);

  useEffect(() => {
    setSelectedItem(null);
  }, [documentId]);

  const handleRequestClose = () => {
    setScenesHistoryOpen(false);
  };

  // New paginated fetch function
  const fetchHistoryPaginated = useCallback(
    async (limit = 50, offset = 0) => {
      if (!documentId || !project?.id) return [];

      const response = await Auth.fetchWithIdToken(`/api/scenes/history`, {
        method: "POST",
        documentId,
        context_project_id: project?.id,
        limit,
        offset,
      });

      if (response.historicalRecords) {
        const mapped = response.historicalRecords
          .map((record) => ({
            ...record,
            key: record.id,
            tiptapContent: record.data.value.scenes,
          }))
          .filter((rec) => !!rec.tiptapContent);
        const mappedBeats = response.historicalBeats.map((beat) => ({ ...beat, id: beat.recordId, key: beat.recordId, tiptapContent: oldBeatsToTiptap(beat) })).filter((rec) => !!rec.tiptapContent);
        return [...mapped, ...mappedBeats];
      }

      return [];
    },
    [documentId, project?.id]
  );

  const handleRestore = () => {
    if (selectedItem) {
      onRestore(selectedItem.tiptapContent);
    }
    setScenesHistoryOpen(false);
  };

  // When using pagination, we need to handle the selected item differently
  const handleSelectItem = (item) => {
    setSelectedItem(item);
  };

  const noHistoricalRecords = ![...historicalRecords, ...historicalBeats].length;
  return (
    <HistoryModal
      isOpen={scenesHistoryOpen}
      onClose={handleRequestClose}
      selectedItem={
        <>
          {!selectedItem ? null : (
            <SelectedItemContainer>
              <SelectedItemDisplay selectedItem={selectedItem} />
              {noHistoricalRecords && !loading && !selectedItem && (
                <div className="no-historical-records">
                  <p>No scene history available for this document</p>
                </div>
              )}
            </SelectedItemContainer>
          )}
        </>
      }
      historyItems={[...historicalRecords, ...historicalBeats]}
      onSelectItem={handleSelectItem}
      onRestoreItem={handleRestore}
      title="Scenes History"
      selectedItemId={null} // Don't pass selectedItemId when using pagination
      sourceTable="scenes"
      loading={loading}
      restoring={false}
      fetchHistory={fetchHistoryPaginated}
      usePagination={true}
    />
  );
}

export default ScenesHistory;
